import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider, CssBaseline, Box, Button, Chip, Typography } from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import theme from "../theme/theme";
import { isAdminAuthenticated } from "../api/adminSession";
import { logout } from "../api/session";

/**
 * Chrome for the admin console.
 *
 * Deliberately leaner than AppShell: no marketing banner, no product nav and
 * no sign-up path, since admins are provisioned rather than self-registered.
 * The theme and animated background are shared so the console still reads as
 * the same product.
 */
export default function AdminShell() {
  const navigate = useNavigate();
  const location = useLocation();

  // The sign-in card is centred; the dashboard needs the full width.
  const isLogin = location.pathname === "/admin/login";
  const signedIn = isAdminAuthenticated();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <div className="op-bg-container">
        <div className="op-grid-bg" />
        <div
          className="op-blob op-blob-a"
          style={{ width: 480, height: 480, top: -140, left: -160, background: "#0D47A1" }}
        />
        <div
          className="op-blob op-blob-b"
          style={{ width: 420, height: 420, bottom: -140, right: -140, background: "#0055FF" }}
        />
      </div>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          bgcolor: "transparent",
        }}
      >
        <Box
          component="header"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: { xs: 2, sm: 4 },
            py: 1.5,
            background: "linear-gradient(93deg,#001645 0%,#0D47A1 55%,#0055FF 100%)",
            boxShadow: "0 6px 24px rgba(0, 22, 69, 0.25)",
          }}
        >
          <Box
            onClick={() => navigate("/")}
            sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer" }}
          >
            <Box
              component="img"
              src="/v_two_white.png"
              alt="OnePass Blueprint"
              sx={{ height: 34, width: "auto" }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Chip
              icon={<ShieldOutlinedIcon />}
              label="Admin Console"
              size="small"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.3,
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.16)",
                border: "1px solid rgba(255,255,255,0.35)",
                "& .MuiChip-icon": { color: "#fff" },
              }}
            />
            {signedIn && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  logout({ redirect: false });
                  navigate("/admin/login", { replace: true });
                }}
                startIcon={<LogoutOutlinedIcon />}
                sx={{
                  // The theme paints outlined buttons solid white, so white
                  // text on this dark header would be invisible. Keep the
                  // white pill and use the navy brand colour for the label,
                  // matching the Logout button in the main app header.
                  bgcolor: "#fff",
                  color: "#001645",
                  borderColor: "rgba(255,255,255,0.6)",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  px: 1.75,
                  "&:hover": {
                    bgcolor: "#f0f4ff",
                    borderColor: "#fff",
                  },
                }}
              >
                Sign Out
              </Button>
            )}
          </Box>
        </Box>

        <Box
          component="main"
          sx={{
            flex: 1,
            display: "flex",
            alignItems: isLogin ? "center" : "flex-start",
            justifyContent: "center",
            py: { xs: 4, sm: 6 },
            px: { xs: 1.5, sm: 2 },
          }}
        >
          <Box sx={{ width: isLogin ? "auto" : "100%" }}>
            <Outlet />
          </Box>
        </Box>

        <Box component="footer" sx={{ py: 2, textAlign: "center" }}>
          <Typography variant="caption" color="text.secondary">
            Restricted area — authorized personnel only.
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
